// client/src/pages/LoginPage.jsx
// Multi-view login page: Password (primary), OTP fallback, Signup, Reset Password.

import React, { useState, useRef, useEffect } from "react";
import { API_BASE_URL } from "../config/apiBase";

// API base URL — uses same env var as api.js, falls back to relative path
const authUrl = (path) => `${API_BASE_URL}/auth/${path}`;

// ─── Views: login | signup | reset | otp ────────────────────────────────────

const LoginPage = ({ onLogin }) => {
  const [view, setView] = useState("login"); // login | signup | reset | otp

  const switchView = (v) => {
    setView(v);
  };

  return (
    <div style={S.container}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', 'DM Sans', system-ui, sans-serif; }
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        input::placeholder { color: #475569; }
        input:focus { outline: none; border-color: rgba(139,92,246,0.5) !important; }
      `}</style>

      <div style={S.card}>
        <LogoHeader />
        <div style={S.formSection}>
          {view === "login" && <LoginView onLogin={onLogin} switchView={switchView} />}
          {view === "signup" && <SignupView onLogin={onLogin} switchView={switchView} />}
          {view === "reset" && <ResetView onLogin={onLogin} switchView={switchView} />}
          {view === "otp" && <OtpView onLogin={onLogin} switchView={switchView} />}
        </div>
        <div style={S.footer}>Shopify + ShipHero Protection</div>
      </div>
    </div>
  );
};

// ─── Logo Header ────────────────────────────────────────────────────────────

const LogoHeader = () => (
  <div style={S.logoWrap}>
    <div style={S.logoIcon}>
      <span role="img" aria-label="shield" style={{ fontSize: 28 }}>{"\uD83D\uDEE1\uFE0F"}</span>
    </div>
    <h1 style={S.logoText}>ShipGuard</h1>
    <p style={S.subtitle}>Order Monitor & Protection System</p>
  </div>
);

// ─── Login View (Password — primary) ────────────────────────────────────────

const LoginView = ({ onLogin, switchView }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(authUrl("login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Login failed.");
      onLogin(data.token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h2 style={S.formTitle}>Sign in to your account</h2>
      <p style={S.formDesc}>Enter your email and password to continue.</p>

      {error && <div style={S.error}><span>{"\u26A0\uFE0F"}</span> {error}</div>}

      <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
        <div style={S.inputWrap}>
          <span style={S.inputIcon}>{"\u2709\uFE0F"}</span>
          <input type="email" placeholder="you@company.com" value={email}
            onChange={(e) => setEmail(e.target.value)} style={S.input}
            autoFocus autoComplete="email" disabled={loading} />
        </div>
        <div style={S.inputWrap}>
          <span style={S.inputIcon}>{"\uD83D\uDD12"}</span>
          <input type="password" placeholder="Password" value={password}
            onChange={(e) => setPassword(e.target.value)} style={S.input}
            autoComplete="current-password" disabled={loading} />
        </div>
        <button type="submit" style={S.primaryBtn} disabled={loading || !email.trim() || !password}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>

      <div style={S.linkRow}>
        <button onClick={() => switchView("reset")} style={S.linkBtn}>Forgot password?</button>
      </div>

      <div style={S.divider}>
        <span style={S.dividerLine} />
        <span style={S.dividerText}>or</span>
        <span style={S.dividerLine} />
      </div>

      <button onClick={() => switchView("otp")} style={S.secondaryBtn}>
        {"\u2709\uFE0F"} Sign in with email code
      </button>

      <div style={{ ...S.linkRow, marginTop: 20 }}>
        <span style={{ color: "#64748b", fontSize: 12 }}>Don't have an account?</span>
        <button onClick={() => switchView("signup")} style={{ ...S.linkBtn, fontWeight: 700 }}>Create Account</button>
      </div>
    </>
  );
};

// ─── Signup View ────────────────────────────────────────────────────────────

const SignupView = ({ onLogin, switchView }) => {
  const [step, setStep] = useState("email"); // email | otp | password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === "otp" && otpRefs.current[0]) {
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) return setError("Please enter your email.");
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(authUrl("request-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send code.");
      setStep("otp");
      setOtp(["", "", "", "", "", ""]);
      setCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyAndSetPassword = async () => {
    // We don't verify OTP separately — we send it with signup
    setStep("password");
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    setError(null);
    try {
      const code = otp.join("");
      const res = await fetch(authUrl("signup"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), code, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Signup failed.");
      onLogin(data.token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h2 style={S.formTitle}>Create your account</h2>
      <p style={S.formDesc}>
        {step === "email" && "Enter your email to get started."}
        {step === "otp" && <>We sent a code to <strong style={{ color: "#8b5cf6" }}>{email}</strong></>}
        {step === "password" && "Set a password for your account."}
      </p>

      {error && <div style={S.error}><span>{"\u26A0\uFE0F"}</span> {error}</div>}

      {step === "email" && (
        <form onSubmit={requestOtp} style={{ marginTop: 20 }}>
          <div style={S.inputWrap}>
            <span style={S.inputIcon}>{"\u2709\uFE0F"}</span>
            <input type="email" placeholder="you@company.com" value={email}
              onChange={(e) => setEmail(e.target.value)} style={S.input}
              autoFocus autoComplete="email" disabled={loading} />
          </div>
          <button type="submit" style={S.primaryBtn} disabled={loading || !email.trim()}>
            {loading ? "Sending..." : "Send Verification Code"}
          </button>
        </form>
      )}

      {step === "otp" && (
        <OtpInput otp={otp} setOtp={setOtp} otpRefs={otpRefs} loading={loading}
          onComplete={verifyAndSetPassword} cooldown={cooldown}
          onResend={requestOtp} />
      )}

      {step === "password" && (
        <form onSubmit={handleSignup} style={{ marginTop: 20 }}>
          <div style={S.inputWrap}>
            <span style={S.inputIcon}>{"\uD83D\uDD12"}</span>
            <input type="password" placeholder="Password (min 8 characters)" value={password}
              onChange={(e) => setPassword(e.target.value)} style={S.input}
              autoFocus autoComplete="new-password" disabled={loading} />
          </div>
          <div style={S.inputWrap}>
            <span style={S.inputIcon}>{"\uD83D\uDD12"}</span>
            <input type="password" placeholder="Confirm password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} style={S.input}
              autoComplete="new-password" disabled={loading} />
          </div>
          <button type="submit" style={S.primaryBtn} disabled={loading || password.length < 8 || password !== confirm}>
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>
      )}

      <div style={{ ...S.linkRow, marginTop: 20 }}>
        <span style={{ color: "#64748b", fontSize: 12 }}>Already have an account?</span>
        <button onClick={() => switchView("login")} style={{ ...S.linkBtn, fontWeight: 700 }}>Sign In</button>
      </div>
    </>
  );
};

// ─── Reset Password View ────────────────────────────────────────────────────

const ResetView = ({ onLogin, switchView }) => {
  const [step, setStep] = useState("email"); // email | otp | password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === "otp" && otpRefs.current[0]) {
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) return setError("Please enter your email.");
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(authUrl("request-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send code.");
      setStep("otp");
      setOtp(["", "", "", "", "", ""]);
      setCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpComplete = () => {
    setStep("password");
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    setError(null);
    try {
      const code = otp.join("");
      const res = await fetch(authUrl("reset-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), code, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Reset failed.");
      onLogin(data.token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h2 style={S.formTitle}>Reset your password</h2>
      <p style={S.formDesc}>
        {step === "email" && "Enter your email to receive a verification code."}
        {step === "otp" && <>We sent a code to <strong style={{ color: "#8b5cf6" }}>{email}</strong></>}
        {step === "password" && "Set your new password."}
      </p>

      {error && <div style={S.error}><span>{"\u26A0\uFE0F"}</span> {error}</div>}

      {step === "email" && (
        <form onSubmit={requestOtp} style={{ marginTop: 20 }}>
          <div style={S.inputWrap}>
            <span style={S.inputIcon}>{"\u2709\uFE0F"}</span>
            <input type="email" placeholder="you@company.com" value={email}
              onChange={(e) => setEmail(e.target.value)} style={S.input}
              autoFocus autoComplete="email" disabled={loading} />
          </div>
          <button type="submit" style={S.primaryBtn} disabled={loading || !email.trim()}>
            {loading ? "Sending..." : "Send Verification Code"}
          </button>
        </form>
      )}

      {step === "otp" && (
        <OtpInput otp={otp} setOtp={setOtp} otpRefs={otpRefs} loading={loading}
          onComplete={handleOtpComplete} cooldown={cooldown}
          onResend={requestOtp} />
      )}

      {step === "password" && (
        <form onSubmit={handleReset} style={{ marginTop: 20 }}>
          <div style={S.inputWrap}>
            <span style={S.inputIcon}>{"\uD83D\uDD12"}</span>
            <input type="password" placeholder="New password (min 8 characters)" value={password}
              onChange={(e) => setPassword(e.target.value)} style={S.input}
              autoFocus autoComplete="new-password" disabled={loading} />
          </div>
          <div style={S.inputWrap}>
            <span style={S.inputIcon}>{"\uD83D\uDD12"}</span>
            <input type="password" placeholder="Confirm new password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} style={S.input}
              autoComplete="new-password" disabled={loading} />
          </div>
          <button type="submit" style={S.primaryBtn} disabled={loading || password.length < 8 || password !== confirm}>
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      )}

      <div style={{ ...S.linkRow, marginTop: 20 }}>
        <button onClick={() => switchView("login")} style={S.linkBtn}>{"\u2190"} Back to Sign In</button>
      </div>
    </>
  );
};

// ─── OTP-only Login View ────────────────────────────────────────────────────

const OtpView = ({ onLogin, switchView }) => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === "otp" && otpRefs.current[0]) {
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) return setError("Please enter your email.");
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(authUrl("request-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send code.");
      setStep("otp");
      setOtp(["", "", "", "", "", ""]);
      setCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(authUrl("verify-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Verification failed.");
      onLogin(data.token, data.user);
    } catch (err) {
      setError(err.message);
      setOtp(["", "", "", "", "", ""]);
      if (otpRefs.current[0]) otpRefs.current[0].focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h2 style={S.formTitle}>Sign in with email code</h2>
      <p style={S.formDesc}>
        {step === "email" && "We'll send a 6-digit code to your email."}
        {step === "otp" && <>Enter the code sent to <strong style={{ color: "#8b5cf6" }}>{email}</strong></>}
      </p>

      {error && <div style={S.error}><span>{"\u26A0\uFE0F"}</span> {error}</div>}

      {step === "email" && (
        <form onSubmit={requestOtp} style={{ marginTop: 20 }}>
          <div style={S.inputWrap}>
            <span style={S.inputIcon}>{"\u2709\uFE0F"}</span>
            <input type="email" placeholder="you@company.com" value={email}
              onChange={(e) => setEmail(e.target.value)} style={S.input}
              autoFocus autoComplete="email" disabled={loading} />
          </div>
          <button type="submit" style={S.primaryBtn} disabled={loading || !email.trim()}>
            {loading ? "Sending..." : "Send Code"}
          </button>
        </form>
      )}

      {step === "otp" && (
        <>
          <OtpInput otp={otp} setOtp={setOtp} otpRefs={otpRefs} loading={loading}
            onComplete={handleVerify} cooldown={cooldown} onResend={requestOtp} />
          {loading && <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 8 }}>Verifying...</div>}
        </>
      )}

      <div style={{ ...S.linkRow, marginTop: 20 }}>
        <button onClick={() => switchView("login")} style={S.linkBtn}>{"\u2190"} Sign in with password</button>
      </div>
    </>
  );
};

// ─── Shared OTP Input Component ─────────────────────────────────────────────

const OtpInput = ({ otp, setOtp, otpRefs, loading, onComplete, cooldown, onResend }) => {
  const handleChange = (idx, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[idx] = digit;
    setOtp(next);
    if (digit && idx < 5 && otpRefs.current[idx + 1]) {
      otpRefs.current[idx + 1].focus();
    }
    const full = next.join("");
    if (full.length === 6 && next.every((d) => d !== "")) {
      onComplete(full);
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter") {
      const code = otp.join("");
      if (code.length === 6) onComplete(code);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      setOtp(pasted.split(""));
      onComplete(pasted);
    }
  };

  return (
    <>
      <div style={S.otpRow} onPaste={handlePaste}>
        {otp.map((digit, i) => (
          <input key={i} ref={(el) => (otpRefs.current[i] = el)}
            type="text" inputMode="numeric" maxLength={1} value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            style={{
              ...S.otpBox,
              borderColor: digit ? "rgba(139,92,246,0.5)" : "rgba(255,255,255,0.08)",
              background: digit ? "rgba(139,92,246,0.08)" : "rgba(255,255,255,0.03)",
            }}
            disabled={loading} />
        ))}
      </div>
      <div style={S.linkRow}>
        <button onClick={onResend}
          disabled={cooldown > 0 || loading}
          style={{ ...S.linkBtn, opacity: cooldown > 0 ? 0.4 : 1, cursor: cooldown > 0 ? "default" : "pointer" }}>
          {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
        </button>
      </div>
    </>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const S = {
  container: {
    minHeight: "100vh",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)",
    padding: 20,
  },
  card: {
    width: "100%", maxWidth: 440,
    background: "rgba(255,255,255,0.03)",
    borderRadius: 24,
    border: "1px solid rgba(139,92,246,0.15)",
    boxShadow: "0 25px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.03) inset",
    overflow: "hidden",
  },
  logoWrap: {
    textAlign: "center",
    padding: "40px 32px 28px",
    background: "linear-gradient(180deg, rgba(139,92,246,0.08) 0%, transparent 100%)",
  },
  logoIcon: {
    width: 56, height: 56, borderRadius: 18,
    background: "linear-gradient(135deg, #8b5cf6, #6366f1)",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    marginBottom: 14,
    boxShadow: "0 8px 32px rgba(99,102,241,0.3)",
  },
  logoText: {
    fontSize: 26, fontWeight: 900, color: "white",
    letterSpacing: "-0.03em", margin: "0 0 4px",
  },
  subtitle: { fontSize: 13, color: "#94a3b8", margin: 0 },
  formSection: { padding: "0 32px 28px", textAlign: "center" },
  formTitle: { fontSize: 17, fontWeight: 700, color: "#e2e8f0", marginBottom: 6 },
  formDesc: { fontSize: 13, color: "#94a3b8", margin: 0, lineHeight: 1.5 },
  error: {
    marginTop: 14, padding: "10px 14px",
    background: "rgba(239,68,68,0.1)",
    border: "1px solid rgba(239,68,68,0.2)",
    borderRadius: 10, color: "#fca5a5", fontSize: 13,
    display: "flex", alignItems: "center", gap: 8, textAlign: "left",
  },
  inputWrap: { position: "relative", marginBottom: 12 },
  inputIcon: {
    position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
    fontSize: 15, pointerEvents: "none",
  },
  input: {
    width: "100%", padding: "13px 14px 13px 40px",
    borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.03)", color: "#e2e8f0",
    fontSize: 14, fontFamily: "inherit", transition: "border-color 0.15s",
  },
  primaryBtn: {
    width: "100%", padding: "13px 16px",
    borderRadius: 10, border: "none",
    background: "linear-gradient(135deg, #8b5cf6, #6366f1)",
    color: "white", fontSize: 14, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
    transition: "opacity 0.15s",
  },
  secondaryBtn: {
    width: "100%", padding: "12px 16px",
    borderRadius: 10, border: "1px solid rgba(139,92,246,0.2)",
    background: "rgba(139,92,246,0.06)",
    color: "#a78bfa", fontSize: 13, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit",
    transition: "all 0.15s",
  },
  linkRow: {
    display: "flex", justifyContent: "center", alignItems: "center",
    gap: 6, marginTop: 12,
  },
  linkBtn: {
    background: "none", border: "none",
    color: "#8b5cf6", fontSize: 12, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit", padding: 0,
  },
  divider: {
    display: "flex", alignItems: "center", gap: 12,
    margin: "20px 0",
  },
  dividerLine: {
    flex: 1, height: 1,
    background: "rgba(255,255,255,0.06)",
  },
  dividerText: { fontSize: 11, color: "#475569", fontWeight: 500 },
  otpRow: {
    display: "flex", justifyContent: "center", gap: 8,
    marginTop: 20, marginBottom: 16,
  },
  otpBox: {
    width: 46, height: 52, borderRadius: 10,
    border: "2px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.03)",
    color: "#e2e8f0", fontSize: 20, fontWeight: 700,
    textAlign: "center", fontFamily: "'Courier New', monospace",
    caretColor: "#8b5cf6", transition: "all 0.15s",
  },
  footer: {
    padding: "14px 32px",
    borderTop: "1px solid rgba(255,255,255,0.04)",
    textAlign: "center", fontSize: 11, color: "#64748b",
  },
};

export default LoginPage;
